import React, { useEffect, useMemo, useState } from 'react';
import { colors } from './theme';

export const AGG_OPTIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'count', label: 'Count' },
  { value: 'average', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
];

const GRAPH_TYPES = [
  { value: 'bar', label: 'Bar' },
  { value: 'line', label: 'Line' },
  { value: 'table', label: 'Table' },
];

const SORT_BY_OPTIONS = [
  { value: 'dimension', label: 'Dimension' },
  { value: 'value', label: 'Value' },
];

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'Asc' },
  { value: 'desc', label: 'Desc' },
];

const DEFAULT_LIMIT_OPTIONS = [10, 25, 50, 100];

const OPERATOR_LABELS = {
  eq: 'Equals',
  neq: 'Not equal',
  contains: 'Contains',
  in: 'In list',
  gte: '>=',
  lte: '<=',
  gt: '>',
  lt: '<',
  exists: 'Exists',
};

export function generateTitle({ xAxis, yAxis, aggregation }) {
  return `${aggregation} ${yAxis} by ${xAxis}`;
}

const getTypeOperators = type => {
  if (type === 'Number' || type === 'Date') {
    return ['eq', 'gte', 'lte', 'gt', 'lt', 'in', 'exists'];
  }
  if (type === 'Boolean') {
    return ['eq', 'neq', 'exists'];
  }
  return ['eq', 'neq', 'contains', 'in', 'exists'];
};

const normalizeFieldMeta = meta => Array.isArray(meta) ? meta : [];

const getFieldType = (fieldMeta, fieldName) => {
  const found = fieldMeta.find(field => field.name === fieldName);
  return found?.type || 'String';
};

function BarChart({ data, yKey }) {
  if (!data || data.length === 0) return null;
  const values = data.map(d => Number(d[yKey]) || 0);
  const max = Math.max(...values, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', height: '180px', gap: '0.45rem' }}>
      {data.map(d => (
        <div key={String(d._id)} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
          <div
            style={{
              height: `${((Number(d[yKey]) || 0) / max) * 100}%`,
              background: 'linear-gradient(180deg, rgba(70,187,137,1), rgba(70,187,137,0.58))',
              borderRadius: '10px 10px 0 0',
              minHeight: '4px',
            }}
          />
          <div style={{ fontSize: '0.72rem', marginTop: '0.4rem', color: colors.textDim, wordBreak: 'break-word' }}>
            {String(d._id)}
          </div>
        </div>
      ))}
    </div>
  );
}

function LineChart({ data, yKey }) {
  if (!data || data.length === 0) return null;
  const width = 640;
  const height = 220;
  const padding = 28;
  const values = data.map(item => Number(item[yKey]) || 0);
  const max = Math.max(...values, 1);
  const stepX = data.length > 1 ? (width - padding * 2) / (data.length - 1) : 0;
  const points = data.map((item, index) => {
    const x = padding + stepX * index;
    const y = height - padding - ((Number(item[yKey]) || 0) / max) * (height - padding * 2);
    return { x, y, label: String(item._id), value: Number(item[yKey]) || 0 };
  });
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <rect width={width} height={height} rx="16" fill="rgba(255,255,255,0.03)" />
      <path d={path} fill="none" stroke={colors.accent} strokeWidth="3" />
      {points.map(point => (
        <g key={point.label}>
          <circle cx={point.x} cy={point.y} r="4.5" fill={colors.accent}>
            <title>{`${point.label}: ${point.value}`}</title>
          </circle>
        </g>
      ))}
    </svg>
  );
}

function DataTable({ data, valueLabel, xAxis }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="ni-table-wrap">
      <table className="ni-table">
        <thead>
          <tr>
            <th>{xAxis}</th>
            <th>{valueLabel}</th>
          </tr>
        </thead>
        <tbody>
          {data.map(row => (
            <tr key={String(row._id)}>
              <td>{String(row._id)}</td>
              <td>{row[valueLabel === 'count' ? 'count' : 'value']}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GraphCard({ graph, editable, onRemove, onChange, fieldOptions, fieldMeta }) {
  const {
    id,
    title,
    collection,
    xAxis,
    yAxis,
    aggregation,
    filters = [],
    dateField,
    startDate,
    endDate,
    graphType = 'bar',
    sortBy = 'dimension',
    sortDirection = 'asc',
    limit = 25,
  } = graph;

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  const [data, setData] = useState([]);
  const [fieldSearch, setFieldSearch] = useState('');

  const collectionFields = useMemo(() => fieldOptions[collection] || [], [collection, fieldOptions]);
  const collectionFieldMeta = useMemo(() => normalizeFieldMeta(fieldMeta?.[collection]), [collection, fieldMeta]);
  const filteredFields = useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    if (!query) return collectionFields;
    return collectionFields.filter(field => field.toLowerCase().includes(query));
  }, [collectionFields, fieldSearch]);

  const valueKey = aggregation === 'count' ? 'count' : 'value';

  const commitTitle = () => {
    setEditingTitle(false);
    onChange(id, { title: titleDraft, customTitle: true });
  };

  const handleChange = (field, value) => {
    const updates = { [field]: value };

    if (field === 'collection') {
      const targetFields = fieldOptions[value] || [];
      const firstField = targetFields[0] || '';
      updates.xAxis = firstField;
      updates.yAxis = firstField;
      updates.filters = [];
      updates.dateField = '';
    }

    if (field === 'aggregation' && value === 'count') {
      updates.yAxis = yAxis || xAxis;
    }

    if (!graph.customTitle && ['collection', 'xAxis', 'yAxis', 'aggregation'].includes(field)) {
      updates.title = generateTitle({ ...graph, ...updates });
    }

    onChange(id, updates);
  };

  const addFilter = () => {
    const firstField = collectionFieldMeta[0]?.name || collectionFields[0] || '';
    if (!firstField) return;
    onChange(id, {
      filters: [...filters, { field: firstField, operator: 'eq', value: '' }]
    });
  };

  const updateFilter = (idx, field, value) => {
    const next = filters.map((item, index) => {
      if (index !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'field') {
        const nextType = getFieldType(collectionFieldMeta, value);
        const operators = getTypeOperators(nextType);
        updated.operator = operators.includes(updated.operator) ? updated.operator : operators[0];
        updated.value = nextType === 'Boolean' ? 'true' : '';
      }
      if (field === 'operator' && value === 'exists') {
        updated.value = 'true';
      }
      return updated;
    });
    onChange(id, { filters: next });
  };

  const removeFilter = idx => {
    onChange(id, { filters: filters.filter((_, index) => index !== idx) });
  };

  useEffect(() => {
    async function fetchData() {
      const params = new URLSearchParams({
        collection,
        xAxis,
        yAxis,
        aggregation,
        sortBy,
        sortDirection,
        limit: String(limit || 25),
      });

      if (filters.length) params.append('filters', JSON.stringify(filters));
      if (dateField && startDate && endDate) {
        params.append('dateField', dateField);
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/analytics/dynamic?${params.toString()}`);
        const json = await res.json();
        setData(Array.isArray(json) ? json : []);
      } catch (error) {
        console.error('Failed to fetch chart data', error);
        setData([]);
      }
    }

    if (xAxis) fetchData();
  }, [collection, xAxis, yAxis, aggregation, filters, dateField, startDate, endDate, sortBy, sortDirection, limit]);

  const renderFilterValueInput = (filter, idx) => {
    const fieldType = getFieldType(collectionFieldMeta, filter.field);
    if (filter.operator === 'exists') {
      return (
        <select
          className="ni-select"
          value={String(filter.value || 'true')}
          onChange={event => updateFilter(idx, 'value', event.target.value)}
          disabled={!editable}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (fieldType === 'Boolean') {
      return (
        <select
          className="ni-select"
          value={String(filter.value || 'true')}
          onChange={event => updateFilter(idx, 'value', event.target.value)}
          disabled={!editable}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    const inputType = fieldType === 'Date' ? 'date' : fieldType === 'Number' ? 'number' : 'text';
    const placeholder = filter.operator === 'in' ? 'comma,separated,values' : 'Value';

    return (
      <input
        className="ni-input"
        type={inputType}
        value={filter.value || ''}
        onChange={event => updateFilter(idx, 'value', event.target.value)}
        disabled={!editable}
        placeholder={placeholder}
      />
    );
  };

  return (
    <div
      style={{
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.02), transparent), rgba(21, 27, 25, 0.96)',
        color: colors.textBody,
        padding: '1rem',
        borderRadius: '18px',
        position: 'relative',
        border: `1px solid ${colors.panelBorder}`,
        boxShadow: colors.shadow,
        display: 'grid',
        gap: '0.85rem'
      }}
    >
      {editable && (
        <button
          onClick={() => onRemove(id)}
          className="ni-button ni-button--danger"
          style={{
            position: 'absolute',
            top: '0.7rem',
            right: '0.7rem',
            padding: '0.45rem 0.8rem'
          }}
        >
          Remove
        </button>
      )}

      <div style={{ paddingRight: editable ? '6rem' : 0 }}>
        {editable && editingTitle ? (
          <input
            value={titleDraft}
            onChange={event => setTitleDraft(event.target.value)}
            onBlur={commitTitle}
            onKeyDown={event => {
              if (event.key === 'Enter') commitTitle();
            }}
            className="ni-input"
            style={{ width: '100%' }}
            autoFocus
          />
        ) : (
          <h3
            style={{ margin: 0, cursor: editable ? 'pointer' : 'default', color: colors.textStrong }}
            onClick={() => editable && setEditingTitle(true)}
          >
            {title}
          </h3>
        )}
        <div style={{ color: colors.textDim, fontSize: '0.82rem', marginTop: '0.3rem' }}>
          Showing {data.length} grouped rows
        </div>
      </div>

      {editable && (
        <>
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
              <select className="ni-select" value={collection} onChange={event => handleChange('collection', event.target.value)}>
                <option value="production">Production</option>
                <option value="rma">RMA</option>
              </select>
              <input
                className="ni-input"
                value={fieldSearch}
                onChange={event => setFieldSearch(event.target.value)}
                placeholder="Search fields"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
              <select className="ni-select" value={xAxis} onChange={event => handleChange('xAxis', event.target.value)}>
                {filteredFields.map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
              <select className="ni-select" value={yAxis} onChange={event => handleChange('yAxis', event.target.value)} disabled={aggregation === 'count'}>
                {filteredFields.map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem' }}>
              <select className="ni-select" value={aggregation} onChange={event => handleChange('aggregation', event.target.value)}>
                {AGG_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select className="ni-select" value={graphType} onChange={event => handleChange('graphType', event.target.value)}>
                {GRAPH_TYPES.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select className="ni-select" value={limit} onChange={event => handleChange('limit', Number(event.target.value))}>
                {DEFAULT_LIMIT_OPTIONS.map(option => (
                  <option key={option} value={option}>Top {option}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem' }}>
              <select className="ni-select" value={sortBy} onChange={event => handleChange('sortBy', event.target.value)}>
                {SORT_BY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select className="ni-select" value={sortDirection} onChange={event => handleChange('sortDirection', event.target.value)}>
                {SORT_DIRECTION_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <select className="ni-select" value={dateField || ''} onChange={event => handleChange('dateField', event.target.value)}>
                <option value="">No date field</option>
                {collectionFieldMeta
                  .filter(field => field.type === 'Date' || field.name.toLowerCase().includes('date') || field.name.toLowerCase().includes('at'))
                  .map(field => (
                    <option key={field.name} value={field.name}>{field.name}</option>
                  ))}
              </select>
            </div>

            {dateField && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                <input className="ni-input" type="date" value={startDate || ''} onChange={event => handleChange('startDate', event.target.value)} />
                <input className="ni-input" type="date" value={endDate || ''} onChange={event => handleChange('endDate', event.target.value)} />
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gap: '0.6rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ color: colors.textStrong, fontWeight: 600 }}>Filters</div>
              <button onClick={addFilter} className="ni-button ni-button--ghost" type="button">
                Add Filter
              </button>
            </div>

            {filters.length === 0 ? (
              <div style={{ color: colors.textDim, fontSize: '0.84rem' }}>
                No filters applied.
              </div>
            ) : (
              filters.map((filter, idx) => {
                const fieldType = getFieldType(collectionFieldMeta, filter.field);
                const allowedOperators = getTypeOperators(fieldType);
                return (
                  <div key={`${filter.field}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.8fr 1fr auto', gap: '0.5rem' }}>
                    <select className="ni-select" value={filter.field} onChange={event => updateFilter(idx, 'field', event.target.value)}>
                      {filteredFields.map(field => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                    <select className="ni-select" value={filter.operator || allowedOperators[0]} onChange={event => updateFilter(idx, 'operator', event.target.value)}>
                      {allowedOperators.map(operator => (
                        <option key={operator} value={operator}>{OPERATOR_LABELS[operator]}</option>
                      ))}
                    </select>
                    {renderFilterValueInput(filter, idx)}
                    <button type="button" onClick={() => removeFilter(idx)} className="ni-button ni-button--danger">
                      Remove
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {graphType === 'line' ? (
        <LineChart data={data} yKey={valueKey} />
      ) : graphType === 'table' ? null : (
        <BarChart data={data} yKey={valueKey} />
      )}

      <DataTable data={data} valueLabel={valueKey === 'count' ? 'count' : aggregation} xAxis={xAxis} />
    </div>
  );
}
